import { t as localeText } from "@/shared/lib/translate";
import { api } from "./client";
import type { components } from "./contract";
import { ApiError, unwrap } from "./errors";

export async function getProject(projectID: string): Promise<components["schemas"]["V2Project"]> {
  return unwrap(await api.GET("/v1/projects/{id}", { params: { path: { id: projectID } } }));
}

export async function listAssetProjects(): Promise<components["schemas"]["V2Project"][]> {
  const projects: components["schemas"]["V2Project"][] = [];
  const seen = new Set<string>();
  let cursor: string | undefined;
  do {
    const page = unwrap(await api.GET("/v1/projects", { params: { query: { limit: 100, ...(cursor ? { cursor } : {}) } } }));
    projects.push(...page.items);
    cursor = page.next_cursor;
    if (cursor && seen.has(cursor)) throw new Error(localeText("interface.projects_couldn_t_finish_loading_try_again_0955e5a9", { defaultValue: "Projects couldn’t finish loading. Try again." }));
    if (cursor) seen.add(cursor);
  } while (cursor);
  return projects;
}

async function changeProjectAssets(projectID: string, assetIDs: string[], add: boolean) {
  for (let attempt = 0; attempt < 3; attempt++) {
    const project = await getProject(projectID);
    const selected = new Set(assetIDs);
    const linked = add ? [...new Set([...project.linked_asset_ids, ...assetIDs])] : project.linked_asset_ids.filter(id => !selected.has(id));
    if (linked.length === project.linked_asset_ids.length) return;
    try {
      unwrap(await api.PATCH("/v1/projects/{id}", { params: { path: { id: projectID } }, body: { expected_version: project.version, linked_asset_ids: linked } }));
      return;
    } catch (error) {
      if (!(error instanceof ApiError && error.status === 409) || attempt === 2) throw error;
    }
  }
}

/** Change library membership while keeping immutable generation/context history. */
export async function moveAssetsToProject(assetIDs: string[], destinationID: string, sourceProjectID?: string) {
  const projects = await listAssetProjects();
  const destination = projects.find(project => project.id === destinationID);
  if (!destination || destination.status !== "active" || destination.can_edit === false) throw new Error(localeText("interface.choose_a_project_you_can_edit_c02914d5", { defaultValue: "Choose a project you can edit." }));
  const selected = new Set(assetIDs);
  const sources = projects.filter(project => project.id !== destinationID && (!sourceProjectID || project.id === sourceProjectID) && project.linked_asset_ids.some(id => selected.has(id)));
  if (sources.some(project => project.can_edit === false || project.status !== "active")) throw new Error(localeText("interface.you_need_edit_access_to_the_current_project_to_m_b153809d", { defaultValue: "You need edit access to the current project to move these files." }));
  // Link first: a failure while removing old memberships must never orphan media.
  await changeProjectAssets(destinationID, assetIDs, true);
  const results = await Promise.allSettled(sources.map(project => changeProjectAssets(project.id, assetIDs, false)));
  if (results.some(result => result.status === "rejected")) throw new Error(localeText("interface.saved_in_the_destination_but_some_files_remain_i_167d5a0b", { defaultValue: "Saved in the destination, but some files remain in their previous project. Retry the move to finish." }));
}

/** Rebase additive links on fresh project state; never replace concurrent work. */
export async function linkProjectContent(projectID: string, assets: string[] = [], reference?: { id: string; remove?: boolean }) {
  for (let attempt = 0; attempt < 3; attempt++) {
    const project = unwrap(await api.GET("/v1/projects/{id}", { params: { path: { id: projectID } } }));
    const selections = reference ? project.reusable_defaults.filter(item => item.reusable_id !== reference.id) : project.reusable_defaults;
    if (reference && !reference.remove) selections.push({ reusable_id: reference.id, role: "reference" });
    try {
      return unwrap(await api.PATCH("/v1/projects/{id}", { params: { path: { id: projectID } }, body: {
        expected_version: project.version,
        ...(assets.length ? { linked_asset_ids: [...new Set([...project.linked_asset_ids, ...assets])] } : {}),
        ...(reference ? { reusable_defaults: selections } : {}),
      } }));
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 409 || attempt === 2) throw error;
    }
  }
  throw new Error(localeText("interface.project_changed_during_linking_616f1f39", { defaultValue: "Project changed during linking" }));
}
